import { DatePicker } from "antd"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import * as Yup from "yup"
import CommonSelect from "../../../../core/common/commonSelect"
import {
  bloodGroup,
  emergencyContactRelation,
  gender,
  mothertongue,
  nationality,
  religion,
} from "../../../../core/common/selectoption/selectoption"
import { getAreas, AreaOption } from "../../../../service/transportAreas"
import { selectSessions } from "../../../../core/data/redux/slice/sessionsSlice"
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice"
import {
  createAdmission,
  getParentByCnic,
  updateAdmission,
} from "../../../../service/admission"
import {
  Class,
  getAllClasses,
  getAllSections,
  Sections,
} from "../../../../service/classes"
import { all_routes } from "../../../router/all_routes"
import { listGroups, type Group } from "../../../../service/group"
import CameraModal from "../../registration/cameraModal"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { useReactToPrint } from "react-to-print"
import { DEFAULT_BRANCH_ID } from "../../../../constant"
import PhoneInput from "../../../../components/PhoneNumber"
import PriorStandingCheck from "./PriorStandingCheck"

export type StudentFormValues = {
  session: string
  firstName: string
  lastName: string
  appliedClass: string
  gender: string
  bFormNo: string
  mobileNO: string
  dateOfBirth: string
  placeOfBirth: string
  bloodGroup: string
  height: string
  weight: string
  religion: string
  category: string
  email: string
  motherTongue: string
  studentPicture?: File
  fatherPicture?: File
  motherPicture?: File
  guardianPicture?: File
  fatherName: string
  fatherCnic: string
  motherCnic: string
  fatherCompanyName: string
  fatherSalary: string
  fatherEmail: string
  fatherPhoneNumber: string
  fatherOccupation: string
  referrer: string
  otherReferrer: string
  motherName: string
  motherEmail: string
  motherCompanyName: string
  motherSalary: string
  motherPhoneNumber: string
  motherOccupation: string
  guardianCnic: string
  guardianName: string
  guardianRelation: string
  guardianCompanyName: string
  guardianSalary: string
  guardianPhoneNumber: string
  guardianEmail: string
  guardianOccupation: string
  city: string
  area: string
  buildingName: string
  blockNo: string
  houseNo: string
  transferCertificate: string
  lastResult: string
  birthCertificate: string
  prevSchoolName: string
  prevSchoolAddress: string
  branch: string
  system: string
  group: string
  nationality: string
  prevClass: string
  emergencyContactRelation: string
  section: string
  hasMedicalIssue: boolean
  allergies: string
  medications: string
  hasSiblings: boolean
  noOfSiblings: string
}

const FORM_INITIAL_VALUES = {
  session: "",
  firstName: "",
  lastName: "",
  appliedClass: "",
  gender: "",
  bFormNo: undefined,
  mobileNO: "",
  dateOfBirth: "",
  placeOfBirth: "",
  bloodGroup: "Unknown",
  height: "",
  weight: "",
  religion: "",
  category: "",
  email: "",
  motherTongue: "",
  studentPicture: null,
  fatherPicture: null,
  motherPicture: null,
  guardianPicture: null,
  fatherName: "",
  fatherCnic: "",
  motherCnic: "",
  fatherCompanyName: "",
  fatherSalary: "",
  fatherEmail: "",
  fatherPhoneNumber: "",
  fatherOccupation: "",
  referrer: "",
  otherReferrer: "",
  motherName: "",
  motherCompanyName: "",
  motherSalary: "",
  motherEmail: "",
  motherPhoneNumber: "",
  motherOccupation: "",
  guardianCnic: "",
  guardianCompanyName: "",
  guardianSalary: "",
  guardianName: "",
  guardianRelation: "",
  guardianPhoneNumber: "",
  guardianEmail: "",
  guardianOccupation: "",
  city: "Karachi",
  area: "",
  buildingName: "",
  blockNo: "",
  houseNo: "",
  transferCertificate: "",
  lastResult: "",
  birthCertificate: "",
  prevSchoolName: "",
  prevSchoolAddress: "",
  branch: DEFAULT_BRANCH_ID,
  system: "",
  group: "",
  nationality: "",
  prevClass: "",
  emergencyContactRelation: "",
  section: "",
  allergies: "",
  hasMedicalIssue: false,
  medications: "",
  hasSiblings: false,
  noOfSiblings: 0,
}

const FILE_FIELDS = [
  "studentPicture",
  "fatherPicture",
  "motherPicture",
  "guardianPicture",
]

// Optional text fields (e.g. fatherEmail, motherEmail) can be stored as `null`
// in the database on existing records. Yup.string() (without `.nullable()`)
// treats `null` as an invalid type — "fatherEmail cannot be null" — even
// though the field is optional. Normalize null -> "" before loading an
// existing record into the form so editing it doesn't trip that validator.
const nullsToEmptyStrings = <T extends Record<string, any>>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj || {}).map(([k, v]) => [k, v === null ? "" : v])
  ) as T

const NewAdmission = () => {
  // Already restricted to the current campus's Systems (Branch.systems).
  const campusSystems = useSelector(selectSystemsForCurrentBranch)
  const sessions = useSelector(selectSessions)
  const routes = all_routes
  const location = useLocation()
  const navigate = useNavigate()
  const [allClass, setAllClass] = useState<Class[]>([])
  const [loading, setLoading] = useState(false)
  const [karachiAreas, setKarachiAreas] = useState<AreaOption[]>([])
  const [guardianType, setGuardianType] = useState("parents")
  const [groupOptions, setGroupOptions] = useState<Group[]>([])
  const [formData, setFormData] = useState({
    ...FORM_INITIAL_VALUES,
    system: campusSystems[0]?._id || "",
  })
  const [sectionOptions, setSectionOptions] = useState<Sections>([])
  const [otherReferrer, setOtherReferrer] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [currentCameraField, setCurrentCameraField] = useState<string | null>(
    null
  )
  const componentRef = useRef<HTMLDivElement>(null)

  const isEdit = location.state && location.state._id
  const isFromRegistration = location.state && location.state.fromRegistration
  const validationSchema = Yup.object().shape({
    session: Yup.string().required("Session is required"),
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string(),
    appliedClass: Yup.string().required("Class is required"),
    gender: Yup.string().required("Gender is required"),
    bFormNo: Yup.string(),
    mobileNO: Yup.string(),
    dateOfBirth: Yup.string().required("Date of birth is required"),
    placeOfBirth: Yup.string().required("Place of birth is required"),
    bloodGroup: Yup.string(),
    height: Yup.string(),
    weight: Yup.string(),
    religion: Yup.string(),
    category: Yup.string(),
    // Optional — auto-generated (GR number + initials) at fee payment if left
    // blank, so it's not required here.
    email: Yup.string().email("Invalid email"),
    motherTongue: Yup.string(),
    system: Yup.string().required("System is required!"),
    section: Yup.string().required("Section is required"),
    studentPicture: Yup.mixed<File>()
      .nullable()
      .test("fileSize", "File too large", file => {
        return file ? file.size <= 5 * 1024 * 1024 : true
      })
      .test("fileType", "Unsupported file format", file => {
        return file ? ["image/jpeg", "image/png"].includes(file.type) : true
      }),
    fatherPicture: Yup.mixed<File>()
      .nullable()
      .test("fileSize", "File too large", file => {
        return file ? file.size <= 5 * 1024 * 1024 : true
      })
      .test("fileType", "Unsupported file format", file => {
        return file ? ["image/jpeg", "image/png"].includes(file.type) : true
      }),
    motherPicture: Yup.mixed<File>()
      .nullable()
      .test("fileSize", "File too large", file => {
        return file ? file.size <= 5 * 1024 * 1024 : true
      })
      .test("fileType", "Unsupported file format", file => {
        return file ? ["image/jpeg", "image/png"].includes(file.type) : true
      }),
    guardianPicture: Yup.mixed<File>()
      .nullable()
      .test("fileSize", "File too large", file => {
        return file ? file.size <= 5 * 1024 * 1024 : true
      })
      .test("fileType", "Unsupported file format", file => {
        return file ? ["image/jpeg", "image/png"].includes(file.type) : true
      }),
    fatherName: Yup.string().required("Father's name is required"),
    fatherCnic: Yup.string().required("Father's CNIC is required"),
    fatherCompanyName: Yup.string(),
    fatherSalary: Yup.string().nullable(),
    fatherEmail: Yup.string().email("Invalid email"),
    fatherPhoneNumber: Yup.string().required(
      "Father's phone number is required"
    ),
    fatherOccupation: Yup.string(),
    referrer: Yup.string(),
    otherReferrer: Yup.string(),
    motherName: Yup.string().required("Mother's name is required"),
    motherCnic: Yup.string().required("Mother's CNIC is required"),
    motherEmail: Yup.string().email("Invalid email"),
    motherCompanyName: Yup.string(),
    motherSalary: Yup.string().nullable().notRequired(),
    motherPhoneNumber: Yup.string(),
    motherOccupation: Yup.string(),
    guardianCnic: Yup.string(),
    guardianName: Yup.string(),
    guardianCompanyName: Yup.string(),
    guardianSalary: Yup.string().nullable().notRequired(),
    guardianRelation: Yup.string(),
    guardianPhoneNumber: Yup.string(),
    guardianEmail: Yup.string().email("Invalid email"),
    guardianOccupation: Yup.string(),
    city: Yup.string().required("City is required"),
    area: Yup.string().required("Area is required"),
    buildingName: Yup.string(),
    blockNo: Yup.string().required("Block number is required"),
    houseNo: Yup.string().required("House number is required"),
    transferCertificate: Yup.string(),
    lastResult: Yup.string(),
    birthCertificate: Yup.string(),
    prevSchoolName: Yup.string(),
    prevSchoolAddress: Yup.string(),
    branch: Yup.string().required("Branch is required"),
    nationality: Yup.string().required("Nationality is required"),
    emergencyContactRelation: Yup.string().required(
      "Emergency contact relation is required"
    ),
    hasMedicalIssue: Yup.boolean(),
    allergies: Yup.string(),
    medications: Yup.string(),
    hasSiblings: Yup.boolean(),
    noOfSiblings: Yup.string(),
  })

  const formik = useFormik({
    initialValues: {
      ...formData,
      mobileNO: formData?.mobileNO?.replace(/^\+92/, "") || "",
      fatherPhoneNumber:
        formData?.fatherPhoneNumber?.replace(/^\+92/, "") || "",
      motherPhoneNumber:
        formData?.motherPhoneNumber?.replace(/^\+92/, "") || "",
      guardianPhoneNumber:
        formData?.guardianPhoneNumber?.replace(/^\+92/, "") || "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const phoneFields = [
          "mobileNO",
          "fatherPhoneNumber",
          "motherPhoneNumber",
          "guardianPhoneNumber",
        ]
        const form = new FormData()

        for (const key in values) {
          if (!FILE_FIELDS.includes(key)) {
            let value = values[key as keyof typeof values]
            if (phoneFields.includes(key)) {
              const stringValue = String(value || "")

              if (!stringValue.trim()) continue

              value = stringValue.startsWith("+92")
                ? stringValue === "+92"
                  ? ""
                  : stringValue
                : `+92${stringValue}`
            }

            if (value === undefined || value === null) continue

            form.append(key, String(value))
          }
        }

        FILE_FIELDS.forEach(item => {
          const value = values[item as keyof typeof values]
          if (value) {
            form.append(item, value as any)
          } else {
            form.append(item, "")
          }
        })

        if (isEdit && !isFromRegistration) {
          await updateAdmission(location.state._id, form)
          toast.success("Admission updated successfully!")
        } else {
          if (location?.state?._id) {
            form.append("registrationId", location?.state?._id)
          }
          await createAdmission(form)
          toast.success("Admission created successfully!")
        }

        navigate(routes.admissionFormList)
      } catch (error: any) {
        if (error.response?.data) {
          const errorData = error.response.data
          const fieldErrors: any = {}

          if (errorData.errors && typeof errorData.errors === "object") {
            Object.keys(errorData.errors).forEach(key => {
              if (errorData.errors[key]) {
                let frontendFieldName = key
                if (key === "phone") {
                  if (
                    formik.values.mobileNO &&
                    formik.values.fatherPhoneNumber &&
                    formik.values.mobileNO === formik.values.fatherPhoneNumber
                  ) {
                    frontendFieldName = "mobileNO"
                  } else if (
                    formik.values.mobileNO &&
                    formik.values.motherPhoneNumber &&
                    formik.values.mobileNO === formik.values.motherPhoneNumber
                  ) {
                    frontendFieldName = "mobileNO"
                  } else if (
                    formik.values.fatherPhoneNumber &&
                    formik.values.motherPhoneNumber &&
                    formik.values.fatherPhoneNumber ===
                      formik.values.motherPhoneNumber
                  ) {
                    frontendFieldName = "fatherPhoneNumber"
                  } else {
                    frontendFieldName = "mobileNO"
                  }
                }

                if (key === "cnic") {
                  frontendFieldName = "fatherCnic"
                }

                fieldErrors[frontendFieldName] = Array.isArray(
                  errorData.errors[key]
                )
                  ? errorData.errors[key][0]
                  : errorData.errors[key]
              }
            })
          }

          const possibleDuplicateFields = [
            "email",
            "fatherCnic",
            "motherCnic",
            "guardianCnic",
            "mobileNO",
            "fatherPhoneNumber",
            "motherPhoneNumber",
            "guardianPhoneNumber",
            "bFormNo",
            "phone",
            "cnic",
          ]

          possibleDuplicateFields.forEach(field => {
            if (errorData[field] && typeof errorData[field] === "string") {
              let frontendFieldName = field
              if (field === "phone") {
                frontendFieldName = "mobileNO"
              } else if (field === "cnic") {
                frontendFieldName = "fatherCnic"
              }

              fieldErrors[frontendFieldName] = errorData[field]
            }
          })

          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors)
            toast.error("Please fix the validation errors")
          } else if (errorData.message) {
            toast.error(errorData.message)
          } else {
            toast.error("An error occurred while submitting the form")
          }
        } else {
          toast.error("Network error occurred")
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const fetchClasses = async (system: string) => {
    setLoading(true)
    try {
      const response = await getAllClasses({ system })
      setAllClass(response.classes)
    } catch (error) {
      console.error("Error fetching classes:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSections = async (session: string) => {
    try {
      const data = await getAllSections({ session })
      setSectionOptions(data.sections)
    } catch (error) {
      console.error("Failed to fetch sections:", error)
    }
  }

  useEffect(() => {
    // Sections vary by session (a class can run different sections year to
    // year), so scope to whichever session this admission is actually for.
    if (formik.values.session) fetchSections(formik.values.session)
  }, [formik.values.session])

  useEffect(() => {
    getAreas()
      .then(setKarachiAreas)
      .catch(() => setKarachiAreas([]))
  }, [])

  const handleOptionChange = (e: any) => {
    formik.handleChange(e)
    if (e.target.value !== "Other") {
      formik.setFieldValue("otherReferrer", "")
      setOtherReferrer(false)
      return
    }
    setOtherReferrer(true)
  }

  const handleOtherTextChange = (e: any) => {
    formik.handleChange(e)
  }

  const handleDateChange = (fieldName: string, date: dayjs.Dayjs | null) => {
    if (date) {
      const formattedDate = date.format("MM-DD-YYYY")
      formik.setFieldValue(fieldName, formattedDate)
    }
  }

  const handleCancel = () => {
    navigate(routes.admissionFormList)
  }

  useEffect(() => {
    if (formik.values.system) {
      fetchClasses(formik.values.system)
      formik.setFieldValue("appliedClass", null)
    }
  }, [formik.values.system])

  // Groups (e.g. Computer/Biology, Pre-Medical/Commerce) only apply to a
  // handful of system+class combinations — show the field only when the
  // selected class actually has any.
  useEffect(() => {
    if (!formik.values.system || !formik.values.appliedClass) {
      setGroupOptions([])
      return
    }
    listGroups({ system: formik.values.system, class: formik.values.appliedClass })
      .then((r) => setGroupOptions(r?.data || []))
      .catch(() => setGroupOptions([]))
  }, [formik.values.system, formik.values.appliedClass])

  useEffect(() => {
    formik.setFieldValue("group", "")
  }, [formik.values.appliedClass])

  const loadData = async () => {
    try {
      const studentData = location.state
      const systemId = studentData.system?._id || studentData.system || ""
      const branchId = studentData.branch?._id || studentData.branch || ""
      const sessionId = studentData.session?._id || studentData.session || ""
      const classId =
        studentData.appliedClass?._id || studentData.appliedClass || ""
      const sectionId = studentData.section?._id || studentData.section || ""
      if (systemId) {
        await fetchClasses(systemId)
      }
      const formValues = {
        ...FORM_INITIAL_VALUES,
        ...nullsToEmptyStrings(studentData),
        system: systemId,
        branch: branchId,
        appliedClass: classId,
        session: sessionId,
        section: sectionId,
        noOfSiblings: studentData.noOfSiblings || 0,
      }

      setFormData(formValues)
      formik.setValues(formValues)

      if (isFromRegistration && studentData.fatherCnic) {
        await fetchParentDetails(studentData.fatherCnic)
      }
    } catch (error) {
      console.error("Error loading data:", error)
      toast.error("Failed to load student data")
    }
  }
   useEffect(() => {
    if (isEdit) {
      loadData();
    }
  }, [isEdit]);
  const handleCapture = (imageSrc: string) => {
    if (currentCameraField) {
      fetch(imageSrc)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `${currentCameraField}.jpg`, {
            type: "image/jpeg",
          })
          formik.setFieldValue(currentCameraField, file)
        })
    }
  }

  const handlePrint = useReactToPrint({ contentRef: componentRef })

  const fetchParentDetails = async (cnic: string) => {
    setLoading(true)

    try {
      const parentData = await getParentByCnic(cnic)
      // The lookup can return null for fields the family never filled in (e.g.
      // mother's email/phone on an older sibling's record). Yup.string()
      // rejects a literal null as a type error ("motherEmail cannot be null")
      // even though the field is optional — normalize null -> "" first.
      parentData.parents.forEach((rawParent: any) => {
        const parent = nullsToEmptyStrings(rawParent)
        if (parent.relation === "father") {
          formik.setFieldValue("fatherName", parent.name)
          formik.setFieldValue("fatherEmail", parent.email)
          formik.setFieldValue("fatherSalary", parent.salary)
          formik.setFieldValue("fatherOccupation", parent.occupation)
          formik.setFieldValue("fatherPhoneNumber", parent.phoneNumber)
          formik.setFieldValue("fatherCompanyName", parent.company)
          formik.setFieldValue("fatherCnic", parent.cnic)
        }
        if (parent.relation === "mother") {
          formik.setFieldValue("motherName", parent.name)
          formik.setFieldValue("motherEmail", parent.email)
          formik.setFieldValue("motherOccupation", parent.occupation)
          formik.setFieldValue("motherPhoneNumber", parent.phoneNumber)
          formik.setFieldValue("motherCnic", parent.cnic)
          formik.setFieldValue("motherCompanyName", parent.motherCompanyName)
          formik.setFieldValue("motherSalary", parent.motherSalary)
        }
      })
      // Address lives on a past admission, not on the parent record — carry it
      // over the same way (only fields the family actually has on file).
      if (parentData.address) {
        const address = nullsToEmptyStrings(parentData.address)
        if (address.city) formik.setFieldValue("city", address.city)
        if (address.area) formik.setFieldValue("area", address.area)
        if (address.buildingName) formik.setFieldValue("buildingName", address.buildingName)
        if (address.blockNo) formik.setFieldValue("blockNo", address.blockNo)
        if (address.houseNo) formik.setFieldValue("houseNo", address.houseNo)
      }
    } catch (err) {
      console.error("err:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleFatherCnicChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Update the field value first
    let value = e.target.value.replace(/\D/g, "")
    if (value.length > 5) {
      value = value.slice(0, 5) + "-" + value.slice(5)
    }
    if (value.length > 13) {
      value = value.slice(0, 13) + "-" + value.slice(13)
    }
    if (value.length > 15) {
      value = value.slice(0, 15)
    }
    formik.setFieldValue("fatherCnic", value)

    if (value.length === 15 && !loading) {
      await fetchParentDetails(value)
    }
  }

  const handleMotherCnicChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value.replace(/\D/g, "")
    if (value.length > 5) {
      value = value.slice(0, 5) + "-" + value.slice(5)
    }
    if (value.length > 13) {
      value = value.slice(0, 13) + "-" + value.slice(13)
    }
    if (value.length > 15) {
      value = value.slice(0, 15)
    }
    formik.setFieldValue("motherCnic", value)
  }

  const handleBformChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "")
    if (value.length > 5) {
      value = value.slice(0, 5) + "-" + value.slice(5)
    }
    if (value.length > 13) {
      value = value.slice(0, 13) + "-" + value.slice(13)
    }
    if (value.length > 15) {
      value = value.slice(0, 15)
    }
    formik.setFieldValue("bFormNo", value)
  }

  const handleGuardianCnicChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Update the field value first
    let value = e.target.value.replace(/\D/g, "")
    if (value.length > 5) {
      value = value.slice(0, 5) + "-" + value.slice(5)
    }
    if (value.length > 13) {
      value = value.slice(0, 13) + "-" + value.slice(13)
    }
    if (value.length > 15) {
      value = value.slice(0, 15)
    }
    formik.setFieldValue("guardianCnic", value)
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <div className="d-flex align-items-center">
                <h3 className="mb-1">
                  {isEdit && !isFromRegistration ? "Edit" : "Add"} Admission
                  Form
                </h3>
                {isEdit && location.state?.temporaryGR && (
                  <span className="badge badge-sm bg-primary ms-2 mb-1">
                    {location.state.temporaryGR}
                  </span>
                )}
              </div>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <span>Admission</span>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit && !isFromRegistration ? "Edit" : "New"} Admission
                  </li>
                </ol>
              </nav>
            </div>
            <div className="pe-1 mb-2">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-top">Print</Tooltip>}
              >
                <button
                  onClick={handlePrint}
                  type="button"
                  className="btn btn-outline-light bg-white btn-icon me-1"
                >
                  <i className="ti ti-printer" />
                </button>
              </OverlayTrigger>
            </div>
          </div>
          {/* A returning student has to be recognised BEFORE the form is
              filled in, because it changes what is charged — a child who left
              and came back pays the re-admission fee. This form never asks for
              a GR (one is generated later), so the check has to be offered
              explicitly rather than triggered by a field. Hidden while
              editing: an existing admission is not a returning one. */}
          {!isEdit && <PriorStandingCheck />}

          <div className="row" ref={componentRef}>
            <div className="col-md-12">
              <form onSubmit={formik.handleSubmit}>
                {/* Personal Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Personal Information</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {formik.values.studentPicture ? (
                              <img
                                src={
                                  typeof formik.values.studentPicture ===
                                  "string"
                                    ? formik.values.studentPicture
                                    : URL.createObjectURL(
                                        formik.values.studentPicture
                                      )
                                }
                                alt="Student"
                                style={{ width: "100%", height: "100%" }}
                              />
                            ) : (
                              <i className="ti ti-photo-plus fs-16" />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center flex-wrap gap-1 mb-3">
                              <div className="drag-upload-btn mb-0">
                                <i className="ti ti-upload me-1"></i> Upload
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="form-control image-sign"
                                  name="studentPicture"
                                  onChange={e => {
                                    if (e.target.files?.[0]) {
                                      formik.setFieldValue(
                                        "studentPicture",
                                        e.target.files[0]
                                      )
                                    }
                                  }}
                                />
                              </div>
                              <div className="drag-upload-btn mb-0">
                                <i className="ti ti-camera me-1"></i> Take Photo
                                <button
                                  type="button"
                                  className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                                  onClick={() => {
                                    setCurrentCameraField("studentPicture")
                                    setShowCamera(true)
                                  }}
                                />
                              </div>
                              {formik.values.studentPicture && (
                                <div
                                  className="btn btn-primary mt-2"
                                  onClick={() => {
                                    formik.setFieldValue("studentPicture", null)
                                  }}
                                >
                                  {location.state?.studentPicture
                                    ? "Restore"
                                    : "Remove"}
                                </div>
                              )}
                            </div>
                            <p className="fs-12">
                              Upload image size 4MB, Format JPG, PNG, SVG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row row-cols-xxl-5 row-cols-md-6">
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Session</label>
                          <CommonSelect
                            options={sessions}
                            value={sessions.find(
                              option => option._id === formik.values.session
                            )}
                            onChange={selectedOption => {
                              formik.setFieldValue(
                                "session",
                                selectedOption?._id || ""
                              )
                            }}
                            getOptionValue={item => item._id}
                            getOptionLabel={item => item.title}
                          />
                          {formik.touched.session && formik.errors.session ? (
                            <div className="text-danger">
                              {formik.errors.session}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">First Name</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.firstName &&
                              formik.errors.firstName
                                ? "is-invalid"
                                : ""
                            }`}
                            name="firstName"
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.firstName &&
                          formik.errors.firstName ? (
                            <div className="text-danger">
                              {formik.errors.firstName}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Last Name</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.lastName && formik.errors.lastName
                                ? "is-invalid"
                                : ""
                            }`}
                            name="lastName"
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.lastName && formik.errors.lastName ? (
                            <div className="text-danger">
                              {formik.errors.lastName}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">System</label>
                          <CommonSelect
                            className="select"
                            options={campusSystems}
                            getOptionValue={item => item._id}
                            getOptionLabel={item => item.name}
                            value={campusSystems.find(
                              system => system._id === formik.values.system
                            )}
                            onChange={selectedOption => {
                              formik.setFieldValue(
                                "system",
                                selectedOption?._id || ""
                              )
                            }}
                            isDisabled={loading}
                          />

                          {formik.touched.system && formik.errors.system ? (
                            <div className="text-danger">
                              {formik.errors.system}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Class/Grade</label>
                          {loading ? (
                            <div>Loading classes...</div>
                          ) : (
                            <>
                              <CommonSelect
                                className="select"
                                options={allClass}
                                getOptionValue={item => item._id}
                                getOptionLabel={item => item.name}
                                value={allClass.find(
                                  selectedClass =>
                                    selectedClass._id ===
                                    formik.values.appliedClass
                                )}
                                onChange={selectedOption => {
                                  formik.setFieldValue(
                                    "appliedClass",
                                    selectedOption?._id || ""
                                  )
                                }}
                                isDisabled={loading || !formik.values.system}
                              />
                            </>
                          )}
                          {formik.touched.appliedClass &&
                          formik.errors.appliedClass ? (
                            <div className="text-danger">
                              {formik.errors.appliedClass}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {groupOptions.length > 0 && (
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Group</label>
                            <CommonSelect
                              className="select"
                              options={groupOptions}
                              getOptionValue={item => item._id}
                              getOptionLabel={item => item.name}
                              value={groupOptions.find(
                                g => g._id === formik.values.group
                              )}
                              onChange={selectedOption => {
                                formik.setFieldValue(
                                  "group",
                                  selectedOption?._id || ""
                                )
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Section</label>
                          <CommonSelect
                            className="select"
                            options={sectionOptions}
                            getOptionValue={item => item._id}
                            getOptionLabel={item => item.name}
                            value={sectionOptions.find(
                              section => section._id === formik.values.section
                            )}
                            onChange={selectedOption => {
                              formik.setFieldValue(
                                "section",
                                selectedOption?._id || ""
                              )
                            }}
                          />
                          {formik.touched.section && formik.errors.section ? (
                            <div className="text-danger">
                              {formik.errors.section}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Gender</label>
                          <CommonSelect
                            className="select"
                            options={gender}
                            value={
                              gender.find(
                                option => option.value === formik.values.gender
                              ) || null
                            }
                            onChange={selectedOption =>
                              formik.setFieldValue(
                                "gender",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.gender && formik.errors.gender ? (
                            <div className="text-danger">
                              {formik.errors.gender}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">BForm / CRMS No</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.bFormNo && formik.errors.bFormNo
                                ? "is-invalid"
                                : ""
                            }`}
                            name="bFormNo"
                            value={formik.values.bFormNo}
                            onChange={handleBformChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.bFormNo && formik.errors.bFormNo ? (
                            <div className="text-danger">
                              {formik.errors.bFormNo}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {/* <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <PhoneInput
                            name="mobileNo"
                            value={formik.values.mobileNO}
                            onChange={(value: any) =>
                              formik.setFieldValue("mobileNO", value)
                            }
                            className={
                              formik.touched.mobileNO && formik.errors.mobileNO
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {formik.touched.mobileNO && formik.errors.mobileNO ? (
                            <div className="text-danger">
                              {formik.errors.mobileNO}
                            </div>
                          ) : null}
                        </div>
                      </div> */}
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Date of Birth</label>
                          <div className="input-icon position-relative">
                            <DatePicker
                              className={`form-control datetimepicker ${
                                formik.touched.dateOfBirth &&
                                formik.errors.dateOfBirth
                                  ? "is-invalid"
                                  : ""
                              }`}
                              format={{
                                format: "DD-MM-YYYY",
                                type: "mask",
                              }}
                              value={
                                formik.values.dateOfBirth
                                  ? dayjs(formik.values.dateOfBirth)
                                  : null
                              }
                              onChange={date =>
                                handleDateChange("dateOfBirth", date)
                              }
                              maxDate={dayjs()}
                              placeholder="Select Date"
                            />
                            <span className="input-icon-addon">
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                          {formik.touched.dateOfBirth &&
                          formik.errors.dateOfBirth ? (
                            <div className="text-danger">
                              {formik.errors.dateOfBirth}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Place of Birth</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.placeOfBirth &&
                              formik.errors.placeOfBirth
                                ? "is-invalid"
                                : ""
                            }`}
                            name="placeOfBirth"
                            value={formik.values.placeOfBirth}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.placeOfBirth &&
                          formik.errors.placeOfBirth ? (
                            <div className="text-danger">
                              {formik.errors.placeOfBirth}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Blood Group</label>
                          <CommonSelect
                            className="select"
                            options={bloodGroup}
                            value={
                              bloodGroup.find(
                                option =>
                                  option.value === formik.values.bloodGroup
                              ) || null
                            }
                            onChange={selectedOption =>
                              formik.setFieldValue(
                                "bloodGroup",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.bloodGroup &&
                          formik.errors.bloodGroup ? (
                            <div className="text-danger">
                              {formik.errors.bloodGroup}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Nationality</label>
                          <CommonSelect
                            className="select"
                            options={nationality}
                            value={
                              nationality.find(
                                option =>
                                  option.value === formik.values.nationality
                              ) || null
                            }
                            onChange={selectedOption =>
                              formik.setFieldValue(
                                "nationality",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.nationality &&
                          formik.errors.nationality ? (
                            <div className="text-danger">
                              {formik.errors.nationality}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Height (Ft)</label>
                          <input
                            type="number"
                            className={`form-control ${
                              formik.touched.height && formik.errors.height
                                ? "is-invalid"
                                : ""
                            }`}
                            name="height"
                            value={formik.values.height}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.height && formik.errors.height ? (
                            <div className="text-danger">
                              {formik.errors.height}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Weight (Kg)</label>
                          <input
                            type="number"
                            className={`form-control ${
                              formik.touched.weight && formik.errors.weight
                                ? "is-invalid"
                                : ""
                            }`}
                            name="weight"
                            value={formik.values.weight}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.weight && formik.errors.weight ? (
                            <div className="text-danger">
                              {formik.errors.weight}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Religion</label>
                          <CommonSelect
                            className="select"
                            options={religion}
                            value={
                              religion.find(
                                option =>
                                  option.value === formik.values.religion
                              ) || null
                            }
                            onChange={selectedOption =>
                              formik.setFieldValue(
                                "religion",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.religion && formik.errors.religion ? (
                            <div className="text-danger">
                              {formik.errors.religion}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Email Address{" "}
                            <span className="text-muted small">
                              (optional — auto-generated if left blank)
                            </span>
                          </label>
                          <input
                            type="email"
                            className={`form-control ${
                              formik.touched.email && formik.errors.email
                                ? "is-invalid"
                                : ""
                            }`}
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.email && formik.errors.email ? (
                            <div className="text-danger">
                              {formik.errors.email}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Mother Tongue</label>
                          <CommonSelect
                            className="select"
                            options={mothertongue}
                            value={
                              mothertongue.find(
                                option =>
                                  option.value === formik.values.motherTongue
                              ) || null
                            }
                            onChange={selectedOption =>
                              formik.setFieldValue(
                                "motherTongue",
                                selectedOption?.value
                              ) || ""
                            }
                          />
                          {formik.touched.motherTongue &&
                          formik.errors.motherTongue ? (
                            <div className="text-danger">
                              {formik.errors.motherTongue}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label text-nowrap">
                            First Person to call in case of Emergency
                          </label>
                          <CommonSelect
                            className="select"
                            options={emergencyContactRelation}
                            value={
                              emergencyContactRelation.find(
                                option =>
                                  option.value ===
                                  formik.values.emergencyContactRelation
                              ) || null
                            }
                            onChange={selectedOption =>
                              formik.setFieldValue(
                                "emergencyContactRelation",
                                selectedOption?.value
                              ) || ""
                            }
                          />
                          {formik.touched.emergencyContactRelation &&
                          formik.errors.emergencyContactRelation ? (
                            <div className="text-danger">
                              {formik.errors.emergencyContactRelation}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Parents & Guardian Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-user-shield fs-16" />
                      </span>
                      <h4 className="text-dark">
                        Parents &amp; Guardian Information
                      </h4>
                    </div>
                  </div>
                  <div className="card-body pb-0">
                    <div className="border-bottom mb-3">
                      <h5 className="mb-3">Father's Info</h5>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                            <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                              {formik.values.fatherPicture ? (
                                <img
                                  src={
                                    typeof formik.values.fatherPicture ===
                                    "string"
                                      ? formik.values.fatherPicture
                                      : URL.createObjectURL(
                                          formik.values.fatherPicture
                                        )
                                  }
                                  alt="Student"
                                  style={{ width: "100%", height: "100%" }}
                                />
                              ) : (
                                <i className="ti ti-photo-plus fs-16" />
                              )}
                            </div>
                            <div className="profile-upload">
                              <div className="profile-uploader d-flex align-items-center flex-wrap gap-1 mb-3">
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-upload me-1"></i> Upload
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control image-sign"
                                    name="fatherPicture"
                                    onChange={e => {
                                      if (e.target.files?.[0]) {
                                        formik.setFieldValue(
                                          "fatherPicture",
                                          e.target.files[0]
                                        )
                                      }
                                    }}
                                  />
                                </div>
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-camera me-1"></i> Take
                                  Photo
                                  <button
                                    type="button"
                                    className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                                    onClick={() => {
                                      setCurrentCameraField("fatherPicture")
                                      setShowCamera(true)
                                    }}
                                  />
                                </div>
                                {formik.values.fatherPicture && (
                                  <div
                                    className="btn btn-primary mt-2"
                                    onClick={() => {
                                      formik.setFieldValue(
                                        "fatherPicture",
                                        null
                                      )
                                    }}
                                  >
                                    {location.state?.studentPicture
                                      ? "Restore"
                                      : "Remove"}
                                  </div>
                                )}
                              </div>
                              <p className="fs-12">
                                Upload image size 4MB, Format JPG, PNG, SVG
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Father CNIC Number
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                formik.touched.fatherCnic &&
                                formik.errors.fatherCnic
                                  ? "is-invalid"
                                  : ""
                              }`}
                              name="fatherCnic"
                              value={formik.values.fatherCnic}
                              onChange={handleFatherCnicChange}
                              onBlur={formik.handleBlur}
                            />
                            {formik.touched.fatherCnic &&
                            formik.errors.fatherCnic ? (
                              <div className="text-danger">
                                {formik.errors.fatherCnic}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Father Name</label>
                            {loading ? (
                              <div>Loading name...</div>
                            ) : (
                              <input
                                type="text"
                                className={`form-control ${
                                  formik.touched.fatherName &&
                                  formik.errors.fatherName
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="fatherName"
                                value={formik.values.fatherName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                            {formik.touched.fatherName &&
                            formik.errors.fatherName ? (
                              <div className="text-danger">
                                {formik.errors.fatherName}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Company Name</label>
                            {loading ? (
                              <div>Loading company name...</div>
                            ) : (
                              <input
                                type="text"
                                className="form-control"
                                name="fatherCompanyName"
                                value={formik.values.fatherCompanyName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Father Salary (PKR)
                            </label>
                            {loading ? (
                              <div>Loading salary...</div>
                            ) : (
                              <input
                                type="number"
                                className="form-control"
                                name="fatherSalary"
                                value={formik.values.fatherSalary}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Email</label>
                            {loading ? (
                              <div>Loading email...</div>
                            ) : (
                              <input
                                type="email"
                                className={`form-control ${
                                  formik.touched.fatherEmail &&
                                  formik.errors.fatherEmail
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="fatherEmail"
                                value={formik.values.fatherEmail}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                            {formik.touched.fatherEmail &&
                            formik.errors.fatherEmail ? (
                              <div className="text-danger">
                                {formik.errors.fatherEmail}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            {loading ? (
                              <div>Loading phone number...</div>
                            ) : (
                              <PhoneInput
                                name="fatherPhoneNumber"
                                value={formik.values.fatherPhoneNumber}
                                onChange={(value: any) =>
                                  formik.setFieldValue(
                                    "fatherPhoneNumber",
                                    value
                                  )
                                }
                                className={
                                  formik.touched.fatherPhoneNumber &&
                                  formik.errors.fatherPhoneNumber
                                    ? "is-invalid"
                                    : ""
                                }
                              />
                            )}
                            {formik.touched.fatherPhoneNumber &&
                            formik.errors.fatherPhoneNumber ? (
                              <div className="text-danger">
                                {formik.errors.fatherPhoneNumber}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Father Occupation
                            </label>
                            {loading ? (
                              <div>Loading occupation...</div>
                            ) : (
                              <input
                                type="text"
                                className="form-control"
                                name="fatherOccupation"
                                value={formik.values.fatherOccupation}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              How did you hear about us ?
                            </label>
                            <select
                              className="form-select"
                              onChange={handleOptionChange}
                              name="referrer"
                              value={formik.values.referrer}
                              onBlur={formik.handleBlur}
                            >
                              <option value="">Select an option</option>
                              <option value="Social Media">Social Media</option>
                              <option value="Friend/Family">
                                Friend/Family
                              </option>
                              <option value="Google Search">
                                Google Search
                              </option>
                              <option value="Advertisement">
                                Advertisement
                              </option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Other</label>
                            <textarea
                              className="form-control"
                              rows={1}
                              value={formik.values.otherReferrer}
                              onChange={handleOtherTextChange}
                              placeholder="Enter Here About Us Other"
                              disabled={!otherReferrer}
                              name="otherReferrer"
                              onBlur={formik.handleBlur}
                              style={{
                                opacity: otherReferrer ? 1 : 0.7,
                                backgroundColor: otherReferrer
                                  ? "white"
                                  : "#f8f9fa",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-bottom mb-3">
                      <h5 className="mb-3">Mother's Info</h5>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                            <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                              {formik.values.motherPicture ? (
                                <img
                                  src={
                                    typeof formik.values.motherPicture ===
                                    "string"
                                      ? formik.values.motherPicture
                                      : URL.createObjectURL(
                                          formik.values.motherPicture
                                        )
                                  }
                                  alt="Student"
                                  style={{ width: "100%", height: "100%" }}
                                />
                              ) : (
                                <i className="ti ti-photo-plus fs-16" />
                              )}
                            </div>
                            <div className="profile-upload">
                              <div className="profile-uploader d-flex align-items-center flex-wrap gap-1 mb-3">
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-upload me-1"></i> Upload
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control image-sign"
                                    name="motherPicture"
                                    onChange={e => {
                                      if (e.target.files?.[0]) {
                                        formik.setFieldValue(
                                          "motherPicture",
                                          e.target.files[0]
                                        )
                                      }
                                    }}
                                  />
                                </div>
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-camera me-1"></i> Take
                                  Photo
                                  <button
                                    type="button"
                                    className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                                    onClick={() => {
                                      setCurrentCameraField("motherPicture")
                                      setShowCamera(true)
                                    }}
                                  />
                                </div>
                                {formik.values.motherPicture && (
                                  <div
                                    className="btn btn-primary mt-2"
                                    onClick={() => {
                                      formik.setFieldValue(
                                        "motherPicture",
                                        null
                                      )
                                    }}
                                  >
                                    {location.state?.studentPicture
                                      ? "Restore"
                                      : "Remove"}
                                  </div>
                                )}
                              </div>
                              <p className="fs-12">
                                Upload image size 4MB, Format JPG, PNG, SVG
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Mother CNIC Number
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                formik.touched.motherCnic &&
                                formik.errors.motherCnic
                                  ? "is-invalid"
                                  : ""
                              }`}
                              name="motherCnic"
                              value={formik.values.motherCnic}
                              onChange={handleMotherCnicChange}
                              onBlur={formik.handleBlur}
                            />

                            {formik.touched.motherCnic &&
                            formik.errors.motherCnic ? (
                              <div className="text-danger">
                                {formik.errors.motherCnic}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Mother Name</label>
                            {loading ? (
                              <div>Loading name...</div>
                            ) : (
                              <input
                                type="text"
                                className={`form-control ${
                                  formik.touched.motherName &&
                                  formik.errors.motherName
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="motherName"
                                value={formik.values.motherName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                            {formik.touched.motherName &&
                            formik.errors.motherName ? (
                              <div className="text-danger">
                                {formik.errors.motherName}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Company Name</label>
                            {loading ? (
                              <div>Loading company name...</div>
                            ) : (
                              <input
                                type="text"
                                className="form-control"
                                name="motherCompanyName"
                                value={formik.values.motherCompanyName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Mother Salary (PKR)
                            </label>
                            {loading ? (
                              <div>Loading salary...</div>
                            ) : (
                              <input
                                type="number"
                                className="form-control"
                                name="motherSalary"
                                value={formik.values.motherSalary}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Email</label>
                            {loading ? (
                              <div>Loading email...</div>
                            ) : (
                              <input
                                type="email"
                                className={`form-control ${
                                  formik.touched.motherEmail &&
                                  formik.errors.motherEmail
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="motherEmail"
                                value={formik.values.motherEmail}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            )}
                            {formik.touched.motherEmail &&
                            formik.errors.motherEmail ? (
                              <div className="text-danger">
                                {formik.errors.motherEmail}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            {loading ? (
                              <div>Loading phone number...</div>
                            ) : (
                              <PhoneInput
                                name="motherPhoneNumber"
                                value={formik.values.motherPhoneNumber}
                                onChange={(value: any) =>
                                  formik.setFieldValue(
                                    "motherPhoneNumber",
                                    value
                                  )
                                }
                                className={
                                  formik.touched.motherPhoneNumber &&
                                  formik.errors.motherPhoneNumber
                                    ? "is-invalid"
                                    : ""
                                }
                              />
                            )}
                            {formik.touched.motherPhoneNumber &&
                            formik.errors.motherPhoneNumber ? (
                              <div className="text-danger">
                                {formik.errors.motherPhoneNumber}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Mother Occupation
                            </label>
                            {loading ? (
                              <div>Loading occupation...</div>
                            ) : (
                              <input
                                type="text"
                                className="form-control"
                                name="motherOccupation"
                                onChange={formik.handleChange}
                                value={formik.values.motherOccupation}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-3">Guardian Details</h5>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-2">
                            <div className="d-flex align-items-center flex-wrap">
                              <label className="form-label text-dark fw-normal me-2">
                                If Guardian Is
                              </label>
                              <div className="form-check me-3 mb-2">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="guardian"
                                  id="parents"
                                  checked={guardianType === "parents"}
                                  onChange={() => setGuardianType("parents")}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="parents"
                                >
                                  Parents
                                </label>
                              </div>
                              <div className="form-check me-3 mb-2">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="guardian"
                                  id="guardian"
                                  checked={guardianType === "guardian"}
                                  onChange={() => setGuardianType("guardian")}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="guardian"
                                >
                                  Guardian
                                </label>
                              </div>
                              <div className="form-check mb-2">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="guardian"
                                  id="other"
                                  checked={guardianType === "other"}
                                  onChange={() => setGuardianType("other")}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="other"
                                >
                                  Others
                                </label>
                              </div>
                            </div>
                          </div>

                          {guardianType !== "parents" && (
                            <>
                              <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                                <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                                  {formik.values.guardianPicture ? (
                                    <img
                                      src={
                                        typeof formik.values.guardianPicture ===
                                        "string"
                                          ? formik.values.guardianPicture
                                          : URL.createObjectURL(
                                              formik.values.guardianPicture
                                            )
                                      }
                                      alt="Guardian"
                                      style={{ width: "100%", height: "100%" }}
                                    />
                                  ) : (
                                    <i className="ti ti-photo-plus fs-16" />
                                  )}
                                </div>
                                <div className="profile-upload">
                                  <div className="profile-uploader d-flex align-items-center flex-wrap gap-1 mb-3">
                                    <div className="drag-upload-btn mb-0">
                                      <i className="ti ti-upload me-1"></i>{" "}
                                      Upload
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="form-control image-sign"
                                        name="guardianPicture"
                                        onChange={e => {
                                          if (e.target.files?.[0]) {
                                            formik.setFieldValue(
                                              "guardianPicture",
                                              e.target.files[0]
                                            )
                                          }
                                        }}
                                      />
                                    </div>
                                    <div className="drag-upload-btn mb-0">
                                      <i className="ti ti-camera me-1"></i> Take
                                      Photo
                                      <button
                                        type="button"
                                        className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                                        onClick={() => {
                                          setCurrentCameraField(
                                            "guardianPicture"
                                          )
                                          setShowCamera(true)
                                        }}
                                      />
                                    </div>
                                    {formik.values.guardianPicture && (
                                      <div
                                        className="btn btn-primary mt-2"
                                        onClick={() => {
                                          formik.setFieldValue(
                                            "guardianPicture",
                                            null
                                          )
                                        }}
                                      >
                                        {location.state?.studentPicture
                                          ? "Restore"
                                          : "Remove"}
                                      </div>
                                    )}
                                  </div>
                                  <p className="fs-12">
                                    Upload image size 4MB, Format JPG, PNG, SVG
                                  </p>
                                </div>
                              </div>

                              <div className="row">
                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Guardian CNIC Number
                                    </label>
                                    <input
                                      type="text"
                                      className={`form-control ${
                                        formik.touched.guardianCnic &&
                                        formik.errors.guardianCnic
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      name="guardianCnic"
                                      value={formik.values.guardianCnic}
                                      onChange={handleGuardianCnicChange}
                                      onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.guardianCnic &&
                                    formik.errors.guardianCnic ? (
                                      <div className="text-danger">
                                        {formik.errors.guardianCnic}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Guardian Name
                                    </label>
                                    <input
                                      type="text"
                                      className={`form-control ${
                                        formik.touched.guardianName &&
                                        formik.errors.guardianName
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      name="guardianName"
                                      value={formik.values.guardianName}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.guardianName &&
                                    formik.errors.guardianName ? (
                                      <div className="text-danger">
                                        {formik.errors.guardianName}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>

                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Guardian Relation
                                    </label>
                                    <input
                                      type="text"
                                      className={`form-control ${
                                        formik.touched.guardianRelation &&
                                        formik.errors.guardianRelation
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      name="guardianRelation"
                                      value={formik.values.guardianRelation}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.guardianRelation &&
                                    formik.errors.guardianRelation ? (
                                      <div className="text-danger">
                                        {formik.errors.guardianRelation}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Company Name
                                    </label>
                                    {loading ? (
                                      <div>Loading company name...</div>
                                    ) : (
                                      <input
                                        type="text"
                                        className="form-control"
                                        name="guardianCompanyName"
                                        value={
                                          formik.values.guardianCompanyName
                                        }
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Guardian Salary (PKR)
                                    </label>
                                    {loading ? (
                                      <div>Loading salary...</div>
                                    ) : (
                                      <input
                                        type="number"
                                        className="form-control"
                                        name="guardianSalary"
                                        value={formik.values.guardianSalary}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                      />
                                    )}
                                  </div>
                                </div>

                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">Email</label>
                                    <input
                                      type="email"
                                      className={`form-control ${
                                        formik.touched.guardianEmail &&
                                        formik.errors.guardianEmail
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      name="guardianEmail"
                                      value={formik.values.guardianEmail}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.guardianEmail &&
                                    formik.errors.guardianEmail ? (
                                      <div className="text-danger">
                                        {formik.errors.guardianEmail}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>

                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <PhoneInput
                                      name="fatherPhoneNumber"
                                      value={formik.values.guardianPhoneNumber}
                                      onChange={(value: any) =>
                                        formik.setFieldValue(
                                          "guardianPhoneNumber",
                                          value
                                        )
                                      }
                                      className={
                                        formik.touched.guardianPhoneNumber &&
                                        formik.errors.guardianPhoneNumber
                                          ? "is-invalid"
                                          : ""
                                      }
                                    />
                                    {formik.touched.guardianPhoneNumber &&
                                    formik.errors.guardianPhoneNumber ? (
                                      <div className="text-danger">
                                        {formik.errors.guardianPhoneNumber}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Occupation
                                    </label>
                                    <input
                                      type="text"
                                      name="guardianOccupation"
                                      className="form-control"
                                      onChange={formik.handleChange}
                                      value={formik.values.guardianOccupation}
                                    />
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Sibilings */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-users fs-16" />
                      </span>
                      <h4 className="text-dark">Siblings Information</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="addsibling-info">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-2">
                            <div className="d-flex align-items-center flex-wrap">
                              <label className="form-label text-dark fw-normal me-2">
                                Does the student have siblings in this school?
                              </label>
                              <div className="form-check mb-2 me-3">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="hasSiblings"
                                  id="hasSiblingsNo"
                                  checked={formik.values.hasSiblings === false}
                                  onChange={() => {
                                    formik.setFieldValue("hasSiblings", false)
                                    formik.setFieldValue("noOfSiblings", 0)
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="hasSiblingsNo"
                                >
                                  No
                                </label>
                              </div>
                              <div className="form-check mb-2">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="hasSiblings"
                                  id="hasSiblingsYes"
                                  checked={formik.values.hasSiblings === true}
                                  onChange={() => {
                                    formik.setFieldValue("hasSiblings", true)
                                    if (!formik.values.noOfSiblings) {
                                      formik.setFieldValue("noOfSiblings", 1)
                                    }
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="hasSiblingsYes"
                                >
                                  Yes
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Show number of siblings input only if hasSiblings is true */}
                          {formik.values.hasSiblings && (
                            <div className="mb-3">
                              <label className="form-label">
                                Number of Siblings
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                name="noOfSiblings"
                                value={formik.values.noOfSiblings}
                                onChange={e => {
                                  const value = parseInt(e.target.value)
                                  formik.setFieldValue("noOfSiblings", value)
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-map fs-16" />
                      </span>
                      <h4 className="text-dark">Current Address</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            House / Flat / Apartment Number
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.houseNo && formik.errors.houseNo
                                ? "is-invalid"
                                : ""
                            }`}
                            name="houseNo"
                            value={formik.values.houseNo}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter House / Flat / Apartment Number"
                          />
                          {formik.touched.houseNo && formik.errors.houseNo ? (
                            <div className="text-danger">
                              {formik.errors.houseNo}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Block Number</label>
                          <input
                            type="number"
                            className={`form-control ${
                              formik.touched.blockNo && formik.errors.blockNo
                                ? "is-invalid"
                                : ""
                            }`}
                            name="blockNo"
                            value={formik.values.blockNo}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter Block No"
                          />
                          {formik.touched.blockNo && formik.errors.blockNo ? (
                            <div className="text-danger">
                              {formik.errors.blockNo}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Building Name / number (If ANY)
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="buildingName"
                            value={formik.values.buildingName}
                            onChange={formik.handleChange}
                            placeholder="Enter Building No"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Area</label>
                          <CommonSelect
                            className="select"
                            options={karachiAreas}
                            value={
                              karachiAreas.find(
                                option => option.value === formik.values.area
                              ) || null
                            }
                            isSearchable={true}
                            onChange={selectedOption =>
                              formik.setFieldValue(
                                "area",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.area && formik.errors.area ? (
                            <div className="text-danger">
                              {formik.errors.area}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">City</label>
                          <select
                            className={`form-select ${
                              formik.touched.city && formik.errors.city
                                ? "is-invalid"
                                : ""
                            }`}
                            name="city"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select an option</option>
                            <option value="Karachi">Karachi</option>
                            <option value="Lahore">Lahore</option>
                            <option value="Islamabad">Islamabad</option>
                            <option value="Multan">Multan</option>
                          </select>
                          {formik.touched.city && formik.errors.city ? (
                            <div className="text-danger">
                              {formik.errors.city}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Medical History */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-medical-cross fs-16" />
                      </span>
                      <h4 className="text-dark">Medical History</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-2 d-flex align-items-center">
                          <label className="form-label mb-0 me-3">
                            Does the student have any medical issues?
                          </label>
                          <div className="d-flex align-items-center">
                            <div className="form-check me-3">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="hasMedicalIssue"
                                id="noMedicalIssues"
                                checked={
                                  formik.values.hasMedicalIssue === false
                                }
                                onChange={() =>
                                  formik.setFieldValue("hasMedicalIssue", false)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="noMedicalIssues"
                              >
                                No
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="hasMedicalIssue"
                                id="hasMedicalIssue"
                                checked={formik.values.hasMedicalIssue === true}
                                onChange={() =>
                                  formik.setFieldValue("hasMedicalIssue", true)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="hasMedicalIssue"
                              >
                                Yes
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Conditional fields shown only when hasMedicalIssue is true */}
                        {formik.values.hasMedicalIssue && (
                          <>
                            <div className="mb-3 mt-3">
                              <label className="form-label">Allergies</label>
                              <input
                                type="text"
                                className="form-control"
                                name="allergies"
                                value={formik.values.allergies}
                                onChange={formik.handleChange}
                                placeholder="List any allergies"
                              />
                            </div>
                            <div className="mb-3 mt-3">
                              <label className="form-label">Medications</label>
                              <input
                                type="text"
                                className="form-control"
                                name="medications"
                                value={formik.values.medications}
                                onChange={formik.handleChange}
                                placeholder="List current medications"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Previous School details */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-building fs-16" />
                      </span>
                      <h4 className="text-dark">Previous School Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">School Name</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.prevSchoolName &&
                              formik.errors.prevSchoolName
                                ? "is-invalid"
                                : ""
                            }`}
                            name="prevSchoolName"
                            value={formik.values.prevSchoolName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.prevSchoolName &&
                          formik.errors.prevSchoolName ? (
                            <div className="text-danger">
                              {formik.errors.prevSchoolName}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {/* <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Address</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.prevSchoolAddress &&
                              formik.errors.prevSchoolAddress
                                ? "is-invalid"
                                : ""
                            }`}
                            name="prevSchoolAddress"
                            value={formik.values.prevSchoolAddress}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.prevSchoolAddress &&
                          formik.errors.prevSchoolAddress ? (
                            <div className="text-danger">
                              {formik.errors.prevSchoolAddress}
                            </div>
                          ) : null}
                        </div>
                      </div> */}
                      {/* <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Class</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.prevClass &&
                              formik.errors.prevClass
                                ? "is-invalid"
                                : ""
                            }`}
                            name="prevClass"
                            value={formik.values.prevClass}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.prevClass &&
                          formik.errors.prevClass ? (
                            <div className="text-danger">
                              {formik.errors.prevClass}
                            </div>
                          ) : null}
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting
                      ? "Submitting..."
                      : !isFromRegistration && isEdit
                      ? "Confirm"
                      : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showCamera && (
        <CameraModal
          onCapture={handleCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </>
  )
}

export default NewAdmission
